import { ControlledDto, ControlledPOSTDto } from 'src/Libs/flow/installation/api'
import { getDefaultModbusTcpEfficientPvSchedule, getDefaultModbusTcpSafePvSchedule } from '../../../constants/default-devices'
import { ChargingSchedule, ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { getLocalStorageValue } from '../../../hooks/useLocalStorageState'
import { parseControlledDevice } from '../device-parsers'
import { getEvsePairingId } from '../get-device-pairing-id'
import { getEvseEfficientPvScheduleLocalStorageKey, getEvseSafePvScheduleLocalStorageKey } from './local-storage-keys'
import { getSunMode, parseChargingModeSettings } from './sun-mode'
import { fromEvseMidMeterToModel } from 'src/Libs/flow/advanced-settings/powermeter'
import { sanitizeToLatin } from 'src/Libs/flow/installation/utils/sanitize-to-latin'
import { mapModbusReferenceToWbType, mapWbTypeToModbusReference, wittyFlexReference } from '../../../../wallboxes/models/wallbox-reference'
import { getChargeInFallbackModeAllowed, getMidMeterParameters } from './modbus-tcp-helpers'

/** DTO to form ModbusTcp */
export const parseModbusTcpChargingStation = (evse: Nullable<ControlledDto>): ModbusTcpChargingStation => {
  const parsedEvse = parseControlledDevice(evse)

  const { chargingMode, chargingWeeklySchedule } = parseChargingModeSettings(evse.parameters?.sunMode ?? null)

  const safePvSchedule: ChargingSchedule =
    chargingMode === 'pvImmediate'
      ? chargingWeeklySchedule
      : getLocalStorageValue<ChargingSchedule>(getEvseSafePvScheduleLocalStorageKey(parsedEvse.deviceId)) ?? getDefaultModbusTcpSafePvSchedule()

  const efficientPvSchedule: ChargingSchedule =
    chargingMode === 'pvDelayed'
      ? chargingWeeklySchedule
      : getLocalStorageValue<ChargingSchedule>(getEvseEfficientPvScheduleLocalStorageKey(parsedEvse.deviceId)) ??
        getDefaultModbusTcpEfficientPvSchedule()

  return {
    ...parsedEvse,

    brand: 'Hager',
    reference: mapWbTypeToModbusReference(evse.evseSubTypeParameters?.wbType),
    deviceReference: evse.evseSubTypeParameters?.deviceReference ?? '',

    protection: evse.parameters?.protection ?? 0,
    holdingTime: evse.parameters?.sunMode?.holdingTimeInMin ?? 0,
    phaseManagement: evse.parameters?.phasesManagement ?? '1PhBlocked',
    phaseMapping: evse.parameters?.phaseMapping ?? '123',
    minCharge: evse.parameters?.minCurrent ?? 0,
    ledIntensityPercentage: evse.parameters?.ledIntensity ?? 0,
    fallbackMaxCurrent: evse.parameters?.fallbackMaxCurrent ?? 0,

    carAtHomeBehavior: {
      isEnabled: evse.evseSubTypeParameters?.ocppActivation ?? false,
      chargingPointOperatorWs: evse.evseSubTypeParameters?.ocppServerAddress || 'wss://',
      ocppId: evse.evseSubTypeParameters?.ocppId || evse.evseSubTypeParameters?.wallboxId || '',
      isCredentialsActive: evse.evseSubTypeParameters?.ocppAuthType === 'basic',
      password: '',
    },

    isCableLocked: evse.parameters?.lockCable ?? false,

    midMeter: fromEvseMidMeterToModel(evse.parameters?.useMidMeter, evse.parameters?.pulseWeight),
    initialAccessManagementOptions: evse.parameters?.authenticationMode ?? 'freeAccess',
    accessManagementOptions: evse.parameters?.authenticationMode ?? 'freeAccess',
    chargingMode,
    safePvSchedule,
    efficientPvSchedule,
    solarRatio: evse.parameters?.minCurrentSunMode ?? 0,
    initialPairingId: getEvsePairingId(evse),
    rawData: evse,
  }
}

/** Form ModbusTcp to DTO */
export const getModbusTcpPOSTDto = (modbusTcp: ModbusTcpChargingStation): ControlledPOSTDto => {
  return {
    deviceName: sanitizeToLatin(modbusTcp.name),
    type: 'Evse',
    media: 'ModbusTCP',
    parameters: {
      protection: modbusTcp.protection || 0,
      minCurrent: modbusTcp.minCharge || 0,
      phasesManagement: modbusTcp.reference === wittyFlexReference ? null : modbusTcp.phaseManagement,
      phaseMapping: modbusTcp.phaseMapping,
      ledIntensity: modbusTcp.ledIntensityPercentage || 0,
      activated: modbusTcp.rawData?.parameters?.activated ?? false,
      authenticationMode: modbusTcp.accessManagementOptions,
      fallbackMaxCurrent: modbusTcp.fallbackMaxCurrent || 0,
      chargeInFallbackModeAllowed: getChargeInFallbackModeAllowed(modbusTcp.fallbackMaxCurrent),
      minCurrentSunMode: modbusTcp.solarRatio || 0,
      priority: modbusTcp.rawData?.parameters?.priority ?? 100,
      ...getMidMeterParameters(modbusTcp),
      sunMode: getSunMode(modbusTcp.holdingTime || 0, modbusTcp.chargingMode, modbusTcp.safePvSchedule, modbusTcp.efficientPvSchedule),
    },
    evseSubTypeParameters: {
      wallboxId: null,
      brand: modbusTcp.brand ?? '',
      wbType: mapModbusReferenceToWbType(modbusTcp.reference),
    },
  }
}
